import { gql } from "@apollo/client";
import { isCustomDomainHost } from "../../pages/TrustCenter/data/publishing";

const publicBase = () =>
  (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");

// Which Trust Center a public call is for: on a customer's own domain the
// page has no slug, so the API finds it by that host instead.
const siteParams = (slug) =>
  isCustomDomainHost()
    ? { host: window.location.hostname.toLowerCase() }
    : { slug };

const visitorHeaders = (token) =>
  token ? { Authorization: `Bearer ${token}` } : {};

const readError = async (response, fallback) => {
  const result = await response.json().catch(() => ({}));
  throw new Error(result.message || fallback);
};

// Public page: plain GET. Pass the visitor session token to open a gated /
// private page (and to see requestable resources after sign-in).
export const fetchPublicTrustCenter = async ({
  slug,
  resourceId,
  token = null,
}) => {
  const params = new URLSearchParams(siteParams(slug));
  if (resourceId) params.set("resource", String(resourceId));
  const response = await fetch(
    `${publicBase()}/api/public/trust/site?${params}`,
    { headers: visitorHeaders(token) }
  );
  if (response.status === 404) return null;
  if (!response.ok) throw new Error("Could not load this Trust Center.");
  return response.json();
};

// Public page downloads: a signed link (valid a few minutes) to one file
// (resourceId), a zip of the chosen ones (resourceIds), or a zip of every
// allowed Public file. With `view` (one resource) the link shows the file in
// the browser when it can (viewable: true). Pass `token` for requestable
// files / gated pages.
// Resolves to { url, fileName, count, viewable }.
export const fetchPublicDownload = async ({
  slug,
  resourceId = null,
  resourceIds = null,
  view = false,
  token = null,
}) => {
  const params = new URLSearchParams(siteParams(slug));
  if (resourceId) params.set("resource", String(resourceId));
  else if (resourceIds?.length) params.set("resources", resourceIds.join(","));
  if (resourceId && view) params.set("view", "1");
  const route = resourceId ? "download" : "download-all";
  const response = await fetch(
    `${publicBase()}/api/public/trust/${route}?${params}`,
    { headers: visitorHeaders(token) }
  );
  if (!response.ok) {
    await readError(
      response,
      `Could not download (server answered ${response.status}). Try again.`
    );
  }
  return response.json();
};

// Visitor sign-in (no Auth0). Body uses slug; answers carry TrustCenterError
// messages from the API.
const postVisitor = async (path, { slug, ...body }, token = null) => {
  const response = await fetch(`${publicBase()}/api/public/trust/${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...visitorHeaders(token),
    },
    body: JSON.stringify({ ...siteParams(slug), ...body }),
  });
  if (!response.ok) {
    await readError(response, "Something went wrong. Try again.");
  }
  return response.json();
};

export const requestVisitorCode = ({ slug, email }) =>
  postVisitor("otp", { slug, email });

export const verifyVisitorCode = ({ slug, email, code }) =>
  postVisitor("verify", { slug, email, code });

export const acceptVisitorNda = ({ slug, token }) =>
  postVisitor("nda", { slug }, token);

export const signOutVisitor = ({ slug, token }) =>
  postVisitor("sign-out", { slug }, token);

// After email + OTP: ask the admin for private content. `token` is the session
// from verify (proves the code).
export const requestTrustCenterAccess = ({
  slug,
  name,
  email,
  company,
  reason,
  resourceId,
  resourceIds,
  token,
}) =>
  postVisitor(
    "request-access",
    { slug, name, email, company, reason, resourceId, resourceIds },
    token
  );

// An "Email only" document: sent to `email` straight away (no code).
export const emailTrustCenterDocument = ({ slug, email, resourceId }) =>
  postVisitor("email-document", { slug, email, resourceId });

// Redeem the personal access link from the grant email (?access= token).
export const redeemTrustCenterAccess = ({ slug, accessToken }) =>
  postVisitor("redeem-access", { slug, accessToken });

// Starts the browser download of a signed link. The link answers with
// "attachment", so the page stays where it is.
export const startDownload = (url) => {
  const link = document.createElement("a");
  link.href = url;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
};

// Admin: stores a file for an "Upload new" resource (multipart REST, like the
// company logo). Returns { documentId, fileName }; the resource is then saved
// with that documentId.
export const uploadTrustCenterDocument = async (
  file,
  { accessToken, idToken }
) => {
  const baseUrl = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
  const formData = new FormData();
  formData.append("file", file);
  const response = await fetch(`${baseUrl}/api/trust-center/documents`, {
    method: "POST",
    headers: { Authorization: `Bearer ${accessToken}`, id_token: idToken },
    body: formData,
  });
  const result = await response.json().catch(() => ({}));
  if (!response.ok) {
    // A route the server does not know answers without a message (e.g. an
    // API that has not been deployed with the upload endpoint yet).
    throw new Error(
      result.message ||
        `Could not upload the file (server answered ${response.status}). Try again.`
    );
  }
  return result;
};

// Admin: logo for a subprocessor added by hand. Returns { logoPath }, which
// is then saved with the vendor. PNG, JPG or WEBP, up to 2 MB.
export const uploadSubprocessorLogo = async (
  file,
  { accessToken, idToken }
) => {
  const baseUrl = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
  const formData = new FormData();
  formData.append("file", file);
  const response = await fetch(
    `${baseUrl}/api/trust-center/subprocessor-logo`,
    {
      method: "POST",
      headers: { Authorization: `Bearer ${accessToken}`, id_token: idToken },
      body: formData,
    }
  );
  const result = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      result.message ||
        `Could not upload the logo (server answered ${response.status}). Try again.`
    );
  }
  return result;
};

// Hero (banner) image for the public page. Returns { heroImagePath }; the
// Overview save puts it in the draft.
// Branding images (multipart REST, like the company logo): the hero banner
// returns { heroImagePath }, the favicon { faviconPath }. Saved with the
// Overview afterwards.
const uploadBrandImage = async (route, file, { accessToken, idToken }) => {
  const baseUrl = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
  const formData = new FormData();
  formData.append("file", file);
  const response = await fetch(`${baseUrl}/api/trust-center/${route}`, {
    method: "POST",
    headers: { Authorization: `Bearer ${accessToken}`, id_token: idToken },
    body: formData,
  });
  const result = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      result.message ||
        `Could not upload the image (server answered ${response.status}). Try again.`
    );
  }
  return result;
};

export const uploadTrustCenterHeroImage = (file, tokens) =>
  uploadBrandImage("hero-image", file, tokens);

export const uploadTrustCenterFavicon = (file, tokens) =>
  uploadBrandImage("favicon", file, tokens);

// One file of a custom font. Returns { fontPath }.
export const uploadTrustCenterFont = (file, tokens) =>
  uploadBrandImage("font", file, tokens);

const SITE_FIELDS = `
    slug
    suggestedSlug
    isPublished
    publishedAt
    publishTarget
    customDomain
    customDomainStatus
    customDomainCheckedAt
    customDomainStage
    customDomainError
    cnameTarget
`;

// Everything on the admin page. Every save mutation returns the same shape.
// Plain field lists (not fragments), shared by the queries below.
const ADMIN_FIELDS = `
    site {
      ${SITE_FIELDS}
    }
    approverEmails
    canApprove
    draft {
      hasUnpublishedChanges
      publishedVersion
      contentPublishedAt
      contentPublishedBy
      changes {
        section
        label
        added
        removed
        changed
      }
    }
    company {
      name
      overview
      website
      securityEmail
      logoPath
      domain
      termsUrl
      privacyUrl
      dataResidency
      heroImagePath
      heroTitle
      brandColor
      secondaryColor
      bannerStyle
      bannerStartColor
      bannerEndColor
      bannerTextColor
      fontFamily
      customFontName
      customFontFiles {
        path
        weight
        style
      }
      faviconPath
      customLinks {
        id
        label
        url
      }
    }
    access {
      mode
      days
      allowedDomains
      invitedEmails {
        email
        invitedAt
      }
    }
    nda {
      required
      version
      text
    }
    resourceCategories {
      id
      name
      description
    }
    resources {
      id
      categoryId
      type
      name
      description
      visibility
      policyId
      sourceLabel
      documentId
      fileName
      url
    }
    frameworks {
      id
      name
      logoPath
      isVisible
      status
      description
      validUntil
      expired
      resourceIds
    }
    subprocessors {
      id
      name
      purpose
      isVisible
      dataLocations
      logoPath
    }
    integrationSubprocessors {
      key
      name
      purpose
      isVisible
      connected
      logoPath
      dataLocations
    }
    faqs {
      id
      question
      answer
    }
    updates {
      id
      title
      category
      body
      status
      publishedAt
    }
    updateSubscriberCount
    controls {
      id
      domain
      name
      title
      description
      status
      frameworkIds
    }
    controlVisibility
    controlsShown
    hiddenControlIds
    endpoints {
      total
      encrypted
    }
`;

export const GET_TRUST_CENTER = gql`
  query getTrustCenter {
    getTrustCenter {
      ${ADMIN_FIELDS}
    }
  }
`;

export const GET_TRUST_CENTER_ACTIVITY = gql`
  query getTrustCenterActivity(
    $limit: Int
    $offset: Int
    $group: TrustCenterLogGroup
  ) {
    getTrustCenterActivity(limit: $limit, offset: $offset, group: $group) {
      total
      items {
        id
        actor
        event
        email
        employeeId
        resourceId
        resourceName
        byEmail
        ipAddress
        metadata
        createdAt
      }
    }
  }
`;

// Settings → Custom domain. The server looks the CNAME up and saves the
// status; each returns the whole admin view.
export const CONNECT_TRUST_CENTER_DOMAIN = gql`
  mutation connectTrustCenterDomain($domain: String!) {
    connectTrustCenterDomain(domain: $domain) {
      ${ADMIN_FIELDS}
    }
  }
`;

export const CHECK_TRUST_CENTER_DOMAIN = gql`
  mutation checkTrustCenterDomain {
    checkTrustCenterDomain {
      ${ADMIN_FIELDS}
    }
  }
`;

export const REMOVE_TRUST_CENTER_DOMAIN = gql`
  mutation removeTrustCenterDomain {
    removeTrustCenterDomain {
      ${ADMIN_FIELDS}
    }
  }
`;

// The customer's admins: who can be chosen to get access request emails.
export const GET_TRUST_CENTER_ADMIN_USERS = gql`
  query getTrustCenterAdminUsers {
    getTrustCenterAdminUsers {
      email
    }
  }
`;

// Who may accept / decline access requests (and is emailed about them).
export const SAVE_TRUST_CENTER_APPROVERS = gql`
  mutation saveTrustCenterApprovers($emails: [String!]!) {
    saveTrustCenterApprovers(emails: $emails) {
      ${ADMIN_FIELDS}
    }
  }
`;

// Evidence attached to checks, offered under Add resource -> From Regodit.
export const GET_TRUST_CENTER_CHECK_DOCUMENTS = gql`
  query getTrustCenterCheckDocuments {
    getTrustCenterCheckDocuments {
      fileId
      fileName
      checkId
      checkName
      uploadedAt
      alreadyAdded
    }
  }
`;

// Makes a check's evidence file a Trust Center document (like an upload).
export const ADD_TRUST_CENTER_CHECK_DOCUMENT = gql`
  mutation addTrustCenterCheckDocument($fileId: Int!) {
    addTrustCenterCheckDocument(fileId: $fileId) {
      documentId
      fileName
    }
  }
`;

export const GET_TRUST_CENTER_ACCESS_REQUESTS = gql`
  query getTrustCenterAccessRequests($limit: Int, $offset: Int) {
    getTrustCenterAccessRequests(limit: $limit, offset: $offset) {
      total
      items {
        id
        name
        email
        company
        reason
        resourceName
        resourceNames
        resourceIds
        status
        accessState
        sharedByAdmin
        requestedAt
        decidedAt
        decidedBy
        revokedAt
      }
    }
  }
`;

export const GRANT_TRUST_CENTER_ACCESS = gql`
  mutation grantTrustCenterAccess($id: Int!) {
    grantTrustCenterAccess(id: $id) {
      id
      name
      email
      company
      status
      accessState
      sharedByAdmin
      requestedAt
      decidedAt
      decidedBy
      revokedAt
    }
  }
`;

export const REVOKE_TRUST_CENTER_ACCESS = gql`
  mutation revokeTrustCenterAccess($id: Int!) {
    revokeTrustCenterAccess(id: $id) {
      id
      name
      email
      company
      status
      accessState
      sharedByAdmin
      requestedAt
      decidedAt
      decidedBy
      revokedAt
    }
  }
`;

export const SHARE_TRUST_CENTER_ACCESS = gql`
  mutation shareTrustCenterAccess($input: TrustCenterShareAccessInput!) {
    shareTrustCenterAccess(input: $input) {
      id
      name
      email
      company
      status
      accessState
      sharedByAdmin
      requestedAt
      decidedAt
      decidedBy
      revokedAt
    }
  }
`;

export const PUBLISH_TRUST_CENTER = gql`
  mutation publishTrustCenter($input: TrustCenterPublishInput!) {
    publishTrustCenter(input: $input) {
      ${SITE_FIELDS}
    }
  }
`;

export const UNPUBLISH_TRUST_CENTER = gql`
  mutation unpublishTrustCenter {
    unpublishTrustCenter {
      ${SITE_FIELDS}
    }
  }
`;

export const SAVE_TRUST_CENTER_OVERVIEW = gql`
  mutation saveTrustCenterOverview($input: TrustCenterOverviewInput!) {
    saveTrustCenterOverview(input: $input) {
      ${ADMIN_FIELDS}
    }
  }
`;

export const SAVE_TRUST_CENTER_ACCESS = gql`
  mutation saveTrustCenterAccess($input: TrustCenterAccessInput!) {
    saveTrustCenterAccess(input: $input) {
      ${ADMIN_FIELDS}
    }
  }
`;

export const SAVE_TRUST_CENTER_CATEGORIES = gql`
  mutation saveTrustCenterResourceCategories(
    $categories: [TrustCenterResourceCategoryInput!]!
  ) {
    saveTrustCenterResourceCategories(categories: $categories) {
      ${ADMIN_FIELDS}
    }
  }
`;

export const SAVE_TRUST_CENTER_RESOURCES = gql`
  mutation saveTrustCenterResources($resources: [TrustCenterResourceInput!]!) {
    saveTrustCenterResources(resources: $resources) {
      ${ADMIN_FIELDS}
    }
  }
`;

export const SAVE_TRUST_CENTER_FRAMEWORKS = gql`
  mutation saveTrustCenterFrameworks(
    $frameworks: [TrustCenterFrameworkInput!]!
  ) {
    saveTrustCenterFrameworks(frameworks: $frameworks) {
      ${ADMIN_FIELDS}
    }
  }
`;

export const SAVE_TRUST_CENTER_SUBPROCESSORS = gql`
  mutation saveTrustCenterSubprocessors(
    $input: TrustCenterSubprocessorsInput!
  ) {
    saveTrustCenterSubprocessors(input: $input) {
      ${ADMIN_FIELDS}
    }
  }
`;

export const SAVE_TRUST_CENTER_FAQS = gql`
  mutation saveTrustCenterFaqs($faqs: [TrustCenterFaqInput!]!) {
    saveTrustCenterFaqs(faqs: $faqs) {
      ${ADMIN_FIELDS}
    }
  }
`;

export const SAVE_TRUST_CENTER_UPDATES = gql`
  mutation saveTrustCenterUpdates($updates: [TrustCenterUpdateInput!]!) {
    saveTrustCenterUpdates(updates: $updates) {
      ${ADMIN_FIELDS}
    }
  }
`;

export const SAVE_TRUST_CENTER_CONTROL_VISIBILITY = gql`
  mutation saveTrustCenterControlVisibility(
    $visibility: TrustCenterControlVisibility!
    $shown: Boolean
    $hiddenControlIds: [Int!]
  ) {
    saveTrustCenterControlVisibility(
      visibility: $visibility
      shown: $shown
      hiddenControlIds: $hiddenControlIds
    ) {
      ${ADMIN_FIELDS}
    }
  }
`;

// Admin and preview: signed link to any resource's file (resourceId), or the
// zip of every Public file that visitors get (no resourceId).
export const GET_TRUST_CENTER_DOWNLOAD = gql`
  query getTrustCenterDownload(
    $resourceId: Int
    $resourceIds: [Int!]
    $view: Boolean
  ) {
    getTrustCenterDownload(
      resourceId: $resourceId
      resourceIds: $resourceIds
      view: $view
    ) {
      url
      fileName
      count
      viewable
    }
  }
`;

// Branding read from a public website (logo, banner, favicon, colours,
// font) for the admin to pick from. Images come back as base64; nothing is
// stored until the admin uploads the ones they choose.
const WEBSITE_IMAGE_FIELDS = `
      contentType
      data
      width
      height
`;

export const GET_WEBSITE_BRANDING = gql`
  query getWebsiteBranding($url: String!) {
    getWebsiteBranding(url: $url) {
      url
      logo {${WEBSITE_IMAGE_FIELDS}}
      bannerImage {${WEBSITE_IMAGE_FIELDS}}
      favicon {${WEBSITE_IMAGE_FIELDS}}
      primaryColor
      primaryColorFound
      secondaryColor
      secondaryColorFound
      fontFamily
      fontName
      customFont {
        name
        source
        files {
          data
          format
          weight
          style
        }
      }
    }
  }
`;
